import { NavLink, useNavigate } from "react-router-dom";
import { LayoutDashboard, Users, FileText, LogOut, ExternalLink, UserCog } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";

const NAV = [
  { to: "/admin", label: "Dashboard", icon: LayoutDashboard, testid: "nav-dashboard", end: true },
  { to: "/admin/clienti", label: "Clienti", icon: Users, testid: "nav-clienti" },
  { to: "/admin/f24", label: "Modelli F24", icon: FileText, testid: "nav-f24" },
];

export default function AdminLayout({ children, title, subtitle }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const navItems = user?.is_super
    ? [...NAV, { to: "/admin/staff", label: "Utenti Studio", icon: UserCog, testid: "nav-staff" }]
    : NAV;

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  return (
    <div className="min-h-screen bg-background">
      <aside className="fixed inset-y-0 left-0 w-64 bg-white border-r border-slate-200 hidden lg:flex flex-col z-30">
        <div className="p-6 border-b border-slate-100">
          <img src="/studio-castelli-logo.jpg" alt="Studio Castelli" className="w-44 h-auto" data-testid="sidebar-logo" />
          <p className="text-[11px] uppercase tracking-[0.2em] text-[#3C763D] mt-2 font-semibold">Area Riservata F24</p>
        </div>
        <nav className="flex-1 p-4 space-y-1">
          {navItems.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              data-testid={item.testid}
              className={({ isActive }) =>
                `flex items-center gap-3 px-4 py-2.5 rounded-lg text-sm font-medium transition-colors duration-200 ${
                  isActive ? "bg-green-50 text-[#3C763D]" : "text-slate-500 hover:bg-green-50/60 hover:text-navy"
                }`
              }
            >
              <item.icon className="w-4 h-4" />
              {item.label}
            </NavLink>
          ))}
        </nav>
        <div className="p-4 border-t border-slate-100 space-y-2">
          <a
            href="https://www.studiocastelli.it"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="back-to-site-link"
            className="flex items-center gap-2 px-4 py-2 text-xs text-slate-400 hover:text-[#3C763D] transition-colors duration-200"
          >
            <ExternalLink className="w-3.5 h-3.5" />
            Torna a studiocastelli.it
          </a>
          <button
            onClick={handleLogout}
            data-testid="logout-button"
            className="flex items-center gap-3 px-4 py-2.5 rounded-lg text-sm font-medium text-slate-500 hover:bg-green-50/60 hover:text-navy transition-colors duration-200 w-full"
          >
            <LogOut className="w-4 h-4" />
            Esci
          </button>
        </div>
      </aside>

      <div className="lg:pl-64">
        <header className="sticky top-0 z-20 backdrop-blur-md bg-white/90 border-b border-slate-200/80">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
            <div>
              <h1 className="font-display text-xl sm:text-2xl font-bold text-navy tracking-tight">{title}</h1>
              {subtitle && <p className="text-xs text-muted-foreground hidden sm:block">{subtitle}</p>}
            </div>
            <div className="flex items-center gap-3">
              <span
                data-testid="user-role-badge"
                className="hidden sm:inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold bg-navy text-gold-bright"
              >
                Admin Studio
              </span>
              <span className="text-sm text-muted-foreground hidden md:block">{user?.name}</span>
              <div className="lg:hidden">
                <Button variant="outline" size="sm" onClick={handleLogout} data-testid="logout-button-mobile">
                  <LogOut className="w-4 h-4" />
                </Button>
              </div>
            </div>
          </div>
          <nav className="lg:hidden border-t border-slate-200/80 px-4 flex gap-1 overflow-x-auto">
            {navItems.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.end}
                data-testid={`${item.testid}-mobile`}
                className={({ isActive }) =>
                  `flex items-center gap-2 px-3 py-2.5 text-sm font-medium border-b-2 whitespace-nowrap transition-colors duration-200 ${
                    isActive ? "border-gold text-navy" : "border-transparent text-muted-foreground"
                  }`
                }
              >
                <item.icon className="w-4 h-4" />
                {item.label}
              </NavLink>
            ))}
          </nav>
        </header>
        <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">{children}</main>
      </div>
    </div>
  );
}
